import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "product-config" }
const _hoisted_2 = {
  key: 0,
  class: "product-config__modal-options"
}
const _hoisted_3 = { class: "product-config__modal-title" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "product-config-components" }
const _hoisted_6 = { class: "product-config__main" }
const _hoisted_7 = { class: "product-config__summary" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = ["disabled"]
const _hoisted_10 = { class: "unfinished-decoration__modal" }
const _hoisted_11 = { class: "unfinished-decoration__modal-title" }
const _hoisted_12 = { class: "unfinished-decoration__modal-options" }

import {
  ref,
  computed,
  provide,
  watch,
  inject,
  onMounted
} from 'vue';
import { inputFocusKey, configuratorStateKey } from './ProductConfiguratorKeys';
import { changeImageKey } from '../ProductOverview/ProductOverviewKeys';
import { setItemKey, routeKey } from '@drapejs/core';
import { useCartFunctions } from '@/helpers/cart-actions';
import useContext from '@/composables/useContext';
import * as config from '@/config';
import useWebsiteTexts from '@/composables/useWebsiteTexts';
import ConfiguratorSummary from './ProductSummary/ConfiguratorSummary.vue';
import ConfiguratorQuantitySelector from './ProductQuantity/ConfiguratorQuantitySelector.vue';
import ConfiguratorPriceBrackets from './ProductPrice/ConfiguratorPriceBrackets.vue';
import ConfiguratorProductDecorator from './ProductDecorations/ConfiguratorProductDecorator.vue';
import ConfiguratorChevron from './ConfiguratorChevron.vue';
import ModalContainer from '@/components/ModalContainer.vue';
import GiftcardTypeSelector from '@/components/Giftcard/GiftcardTypeSelector.vue';
import ProductInformation from './ProductInformation/ProductInformation.vue';
import GiftcardCountrySelector from '../Giftcard/GiftcardCountrySelector.vue';
import GiftcardDeliverytypeSelector from '../Giftcard/GiftcardDeliverytypeSelector.vue';
import GiftcardDesignAddon from '../Giftcard/Addons/GiftcardDesignAddon.vue';
import GiftcardGreetingAddon from '../Giftcard/Addons/GiftcardGreetingAddon.vue';
import GiftcardBoxAddon from '../Giftcard/Addons/GiftcardBoxAddon.vue';
import ErrorMessage from '../forms/ErrorMessage.vue';

interface ConfiguratorState {
  selections: ConfiguratorSelections;
  decorationSteps: ConfiguratorDecorationSteps;
  quantities: VariantQuantities;
  sumQuantity: 0;
  activePriceBracket: PriceBracket;
  supportedVariants: [];
  desiredWidth: 0;
  desiredHeight: 0;
  customPrice: 0;
  recipientCountry: RecipientCountry;
  giftcardType: string;
}

interface ConfiguratorSelections {
  selectedPlacement: string;
  selectedPlacementName: string;
  selectedTechnique: string;
  selectedTechniqueVariant: string;
  selectedTechniqueVariantPrices: [];
  selectedTechniqueVariantAddons: [];
  selectedTechniqueVariantInputColors: {};
  selectedAddons: [];
  fileUpload: FileUpload | null;
  desiredWidth: 0;
  desiredHeight: 0;
}

interface ConfiguratorDecorationSteps {
  initialState: boolean;
  placeDecoration: boolean;
  pickTechnique: boolean;
  uploadDecoration: boolean;
  confirmDecoration: boolean;
  decorationSummary: boolean;
}

interface VariantQuantities {
  variantSystemId: string;
  quantity: number;
}

interface FileUpload {
  Name: string;
  SystemId: string;
  ThumbnailUrl: string;
  Url: string;
}

interface PriceBracket {
  name: string;
  price: number;
  decorationPrice: number;
}

interface RecipientCountry {
  id: string;
  name: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductConfigurator',
  props: {
  configuratorSavedState: {
    type: Object,
    default: undefined
  },
  decorations: {
    type: Array,
    required: true
  },
  addons: {
    type: Array,
    required: true
  },
  variants: {
    type: Array,
    required: true
  },
  modal: {
    type: Boolean,
    default: false
  },
  baseProductName: {
    type: String,
    default: ''
  },
  baseProductSystemId: {
    type: String,
    default: ''
  },
  selectedVariant: {
    type: Object,
    default: {}
  },
  isGiftCard: {
    type: Boolean,
    default: false
  },
  giftCardFreightPrice: {
    type: Number,
    default: 0
  }
},
  emits: ['removeGroup'],
  setup(__props, { emit: __emit }) {

const { isAuthorized, navigateToLoginPage, channel, page, cart, user } =
  useContext();
const { websiteText } = useWebsiteTexts();
const { addItemToCart } = useCartFunctions();
const registerModal = inject('registerModal');
const unregisterModal = inject('unregisterModal');
const route = inject(routeKey);
const setItem = inject(setItemKey);
const updateImagesFilter = inject('changeImageKey');
const emit = __emit;
const isClientRender = typeof window !== 'undefined';

const props = __props;

const productsEdited = ref(false);
const inputFocus = ref(false);
const isDecorating = ref(false);
const unfinishedDecorationModalOpen = ref(false);
const description = computed(() => props.selectedVariant?.fields?._description);
const hasDecoration = computed(
  () => !!configuratorState.value.selections.selectedTechniqueVariant
);
const hasAddons = computed(
  () => configuratorState.value.selections.selectedAddons.length > 0
);
const firstVariant = computed(() =>
  props.variants?.length > 0 ? props.variants[0] : {}
);
const canAddToCart = computed(() => configuratorState.value.sumQuantity > 0);
const decorationsExist = computed(() => !!props.decorations.length);
const designAddonComponent = ref(null);
const greetingAddonComponent = ref(null);
const boxAddonComponent = ref(null);
const typeSelectorComponent = ref(null);
const descriptionReadMoreEnabled = computed(
  () => description?.value?.length > 500
);
const descriptionReadMore = ref(false);
const isFlexible = computed(
  () => props.variants.filter((v) => v.fields.IsFlexibleType).length > 0
);

function initialVariantQuantity(): VariantQuantities {
  return props.variants.reduce((acc, v) => {
    acc[v.systemId] = 0;
    return acc;
  }, {});
}

function initialActivePriceBrackets(): PriceBracket {
  return props.variants.reduce((acc, v) => {
    acc[v.systemId] = {};
    return acc;
  }, {});
}

function initialCustomPrice() {
  return (
    props.selectedVariant?.price ??
    props.selectedVariant?.fields?.MinGiftCardPrice ??
    0
  ).toString();
}

const outOfPrintStock = computed(() => {
  const totalPrintStock = props.variants.reduce((acc, variant) => {
    const currentStock = variant.printableStock?.currentStock?.amount;
    const incomingStock = variant.printableStock?.incomingStock;
    if (currentStock) {
      acc += currentStock;
    }

    if (incomingStock?.length) {
      incomingStock.forEach((i) => {
        if (i.amount) {
          acc += i.amount;
        }
      });
    }

    return acc;
  }, 0);

  return totalPrintStock === 0;
});

const addonMinimumQuantityFullfilled = computed(() => {
  return configuratorState.value.selections.selectedAddons.every(
    (a) =>
      a.variant.minimumQuantity <= configuratorState.value.sumQuantity ||
      a.variant.minimumQuantity == 0
  );
});

const variantMaximumQuantityFullfilled = computed(() => {
  const maxAmount = props.selectedVariant.fields.MaxGiftCardAmount;
  return !maxAmount || configuratorState.value.sumQuantity <= maxAmount;
});

const orderRequirementsFullfilled = computed(() => {
  return (
    addonMinimumQuantityFullfilled.value &&
    variantMaximumQuantityFullfilled.value
  );
});

function getDefaultConfiguratorState() {
  return {
    selections: {
      selectedPlacement: '',
      selectedPlacementName: '',
      selectedTechnique: '',
      selectedTechniqueVariant: '',
      selectedTechniqueVariantPrices: [],
      selectedTechniqueVariantAddons: [],
      selectedTechniqueVariantInputColors: {},
      selectedAddons: [],
      fileUpload: null,
      desiredWidth: 0,
      desiredHeight: 0
    },
    decorationSteps: {
      initialState: true,
      placeDecoration: false,
      pickTechnique: false,
      uploadDecoration: false,
      confirmDecoration: false,
      decorationSummary: false
    },
    quantities: initialVariantQuantity(),
    sumQuantity: 0,
    activePriceBracket: initialActivePriceBrackets(),
    supportedVariants: [],
    desiredWidth: 0,
    desiredHeight: 0,
    customPrice: initialCustomPrice(),
    recipientCountry: {},
    giftcardType: 'Physical'
  };
}

const configuratorState = ref<ConfiguratorState>(
  props.configuratorSavedState || getDefaultConfiguratorState()
);

watch(
  () => props.baseProductSystemId,
  () => {
    configuratorState.value = getDefaultConfiguratorState();
    isDecorating.value = false;
  }
);

watch(
  () => page.value,
  () => {
    refreshAllPrices();
  }
);

watch(
  () => props.variants,
  () => {
    refreshAllPrices();
  }
);

function recalculateDecorationPrices(decorations?: Array) {
  if (
    !decorations?.length ||
    !configuratorState.value.selections.selectedTechniqueVariant
  )
    return;

  const selection = configuratorState.value.selections;
  const currentTechiqueVariant = decorations
    .find((d) => d.systemId === selection.selectedPlacement)
    .techniques.find((t) => t.systemId === selection.selectedTechnique)
    .variants.find((v) => v.systemId === selection.selectedTechniqueVariant);

  if (!currentTechiqueVariant) return;

  selection.selectedTechniqueVariantPrices.forEach(
    (p, idx) => (p.price = currentTechiqueVariant.prices[idx].price)
  );

  selection.selectedTechniqueVariantAddons.forEach(
    (a, idx) => (a.price = currentTechiqueVariant.addons[idx].price)
  );
}

function updateFocusState(val: boolean) {
  inputFocus.value = val;
}

function updateSelection(fieldName: string, val: any) {
  configuratorState.value.selections[fieldName] = val;
}

function updateDecorationSteps(fieldName: string, val: boolean) {
  configuratorState.value.decorationSteps[fieldName] = val;
}

function updateQuantity(variantId: string, val: number) {
  configuratorState.value.quantities[variantId] = val;
  configuratorState.value.sumQuantity = getVariantsToShow().reduce(
    (acc, curr) => acc + configuratorState.value.quantities[curr.systemId],
    0
  );
  updateActivePriceBrackets();
}

function updateCustomPrice(val: number) {
  configuratorState.value.customPrice = val;
}

function removeUpload() {
  if (
    !channel.value?.systemId ||
    !configuratorState.value.selections.fileUpload?.systemId
  )
    return;

  const requestContext = {
    channelSystemId: channel.value.systemId,
    websiteSystemId: channel.value.website?.systemId,
    culture: channel.value.locale
  };

  fetch(
    `${config.litiumBaseUrl}/api/productprint?fileSystemId=${configuratorState.value.selections.fileUpload.systemId}`,
    {
      method: 'DELETE',
      headers: {
        'litium-request-context': JSON.stringify(requestContext)
      }
    }
  )
    .then((response) => {
      if (!response.ok) {
        throw Error(response.statusText);
      }

      return response.json();
    })
    .catch((error) => {
      console.log(error);
    });
}

function removeDecoration() {
  const selection = configuratorState.value.selections;
  selection.selectedPlacement = '';
  selection.selectedPlacementName = '';
  selection.selectedTechnique = '';
  selection.selectedTechniqueVariant = '';
  selection.selectedTechniqueVariantPrices = [];
  selection.selectedTechniqueVariantAddons = [];
  selection.selectedTechniqueVariantInputColors = {};
  selection.desiredWidth = 0;
  selection.desiredHeight = 0;
  selection.fileUpload = null;
  isDecorating.value = false;
}

function getCurrentPriceBracket(amount: number, brackets: any) {
  const bracket = brackets?.reduce(
    (curr, b) => {
      return b.bracket > curr.bracket && b.bracket <= amount ? b : curr;
    },
    { bracket: 0, price: 0 }
  );
  return bracket ?? {};
}

function updateActivePriceBrackets() {
  const activeVariants = props.variants.filter(
    (v) => configuratorState.value.quantities[v.systemId] > 0
  );
  activeVariants.forEach((variant) => {
    const priceBracket = getCurrentPriceBracket(
      configuratorState.value.sumQuantity,
      variant.prices
    );
    configuratorState.value.activePriceBracket[variant.systemId] = priceBracket;
  });
}

async function checkDecorationProgressAndAddToCart() {
  if (
    configuratorState.value.decorationSteps.decorationSummary ||
    !isDecorating.value
  ) {
    addToCart();
  } else {
    openConfirmationModal();
  }
}

function goToStep(step: string) {
  Object.keys(configuratorState.value.decorationSteps).forEach((k) => {
    updateDecorationSteps(k, k === step);
  });
}

function openConfirmationModal() {
  registerModal('UnfinishedDecoration');
  unfinishedDecorationModalOpen.value = true;
}

function closeConfirmationModal() {
  unfinishedDecorationModalOpen.value = false;
  unregisterModal('UnfinishedDecoration');
}

function cancelConfirmation() {
  removeUpload();
  removeDecoration();
  goToStep('initialState');
  closeConfirmationModal();
  configuratorState.value.supportedVariants = [];
}

function navigateToLogin() {
  const search = route?.query || {};
  const query = {
    ...search,
    progress: 'product-configuration'
  };
  sessionStorage.setItem(
    'product-configuration',
    JSON.stringify(configuratorState.value)
  );
  navigateToLoginPage(query);
}

async function addToCart() {
  if (!isAuthorized()) {
    navigateToLogin();
    return;
  }
  
  const variantsToAdd = Object.entries(configuratorState.value.quantities)
    .filter(([k, v]) => v)
    .map(([k, v]) => {
      const articleNumber = props.variants.find(v => v.systemId == k)?.articleNumber;
      
      return { VariantSystemId: k, Quantity: v, ArticleNumber:  articleNumber}});
  const variantData = {
    articleNumber: '',
    variantSystemId: '',
    variants: JSON.stringify(variantsToAdd),
    quantity: 0,
    giftCardInfo: {}
  };

  if (props.isGiftCard) {
    const giftCardInfo = {
      RecipientCountry: configuratorState.value.recipientCountry.id,
      GiftCardType: configuratorState.value.giftcardType,
      Addons: configuratorState.value.selections?.selectedAddons?.map(
        (addon) => {
          return {
            id: addon.variant.id,
            name: addon.variant.name,
            systemId: addon.variant.systemId,
            price: addon.variant.prices[0]?.price || 0,
            quantity: addon.variant.ignoreGiftCardQuantity
              ? 1
              : configuratorState.value.sumQuantity,
            extraInfo: addon.variant.extraInfo
          };
        }
      ),
      GiftCardShipping: {
        id: 'PKFRAKT',
        quantity: configuratorState.value.sumQuantity,
        price: props.giftCardFreightPrice
      },
      CustomPrice: configuratorState.value.customPrice,
      IsFlexible: isFlexible.value
    };
    variantData.giftCardInfo = giftCardInfo;
  } else {
    variantData.giftCardInfo = {};
  }

  const selections = configuratorState.value.selections;
  const selectedPrintData = {
    printVariantSystemId: selections.selectedTechniqueVariant,
    printProductSystemId: selections.selectedTechnique,
    printImageSystemId: selections.fileUpload?.systemId || '',
    printAddons: selections.selectedTechniqueVariantAddons,
    printPosition: selections.selectedPlacementName,
    printPreviewDataUrl: '',
    printText: '',
    desiredWidth: selections.desiredWidth,
    desiredHeight: selections.desiredHeight,
    colors: JSON.stringify(selections.selectedTechniqueVariantInputColors || {})
  };

  const { row, error } = await addItemToCart(variantData, selectedPrintData);

  if (error !== 'NONE') {
    if (error === 'NOT_ENOUGH_STOCK') {
      console.log('NOT_ENOUGH_STOCK');
    } else if (error === 'NOT_AUTHENTICATED') {
      console.log('NOT_AUTHENTICATED');
    } else {
      console.log(error);
    }
    return;
  }

  setItem(
    `__configurator_${row.rowGroupId}`,
    JSON.stringify(configuratorState.value)
  );

  configuratorState.value.quantities = initialVariantQuantity();
  configuratorState.value.sumQuantity = 0;
  configuratorState.value.activePriceBracket = initialActivePriceBrackets();

  Object.keys(configuratorState.value.decorationSteps).forEach((k) => {
    updateDecorationSteps(k, k === 'initialState');
  });

  resetFields();
  removeDecoration();
}

function toggleReadMoreDescription() {
  descriptionReadMore.value = !descriptionReadMore.value;
}

function begunDecoration() {
  isDecorating.value = true;
}

async function checkDecorationProgressAndEditCart() {
  if (
    configuratorState.value.decorationSteps.decorationSummary ||
    !isDecorating.value
  ) {
    emit('removeGroup');
    addToCart();
  } else {
    openConfirmationModal();
  }
}

function editProductsInCartGroup() {
  if (!isAuthorized()) {
    navigateToLogin();
    return;
  }
  checkDecorationProgressAndEditCart();
}

function techniqueVariantSelected(variant) {
  configuratorState.value.supportedVariants = variant.supportedVariants;
}

function getVariantsToShow() {
  if (!props.isGiftCard) return props.variants;
  if (props.selectedVariant !== undefined) return [props.selectedVariant];
  return [];
}

function changeRecipientCountry(country) {
  configuratorState.value.recipientCountry = country;
}

function changeGiftcardType(type) {
  configuratorState.value.giftcardType = type;
}

function addAddonSelection(addon: string, variant: string, extraInfo: string) {
  const propsAddon = props.addons.filter((a) => a.id === addon)[0];
  const addonVariant = propsAddon.variants.filter((v) => v.id === variant)[0];
  updateImagesFilter(variant);
  removeAddonSelection(addon);
  configuratorState.value.selections.selectedAddons.push({
    id: propsAddon.id,
    name: propsAddon.name,
    systemId: propsAddon.systemId,
    variant: {
      ...addonVariant,
      extraInfo
    }
  });
}

function removeAddonSelection(addon: string) {
  configuratorState.value.selections.selectedAddons =
    configuratorState.value.selections.selectedAddons.filter(
      (a) => a.id !== addon
    );
}

function refreshAllPrices() {
  configuratorState.value.selections.selectedAddons.forEach((a) => {
    addAddonSelection(a.id, a.variant.id, null);
  });
  updateActivePriceBrackets();
  recalculateDecorationPrices(page.value?.dataJson?.dataJson?.product?.fields?.Decorations);
}

watch(
  () => props.selectedVariant,
  (next, curr) => {
    if (next.articleNumber !== curr.articleNumber) {
      updateQuantity(curr.systemId, 0);
    }
  }
);

watch(
  configuratorState,
  () => {
    if (!productsEdited.value) {
      productsEdited.value = true;
    }
  },
  { deep: true, immediate: true }
);

onMounted(() => {
  const savedState = sessionStorage.getItem('product-configuration');
  sessionStorage.removeItem('product-configuration');
  if (savedState) {
    configuratorState.value = { ...JSON.parse(savedState) };

    if (!configuratorState.value.decorationSteps.initialState) {
      isDecorating.value = true;
    }
  }
  refreshAllPrices();
});

const selectedDecorationName = computed(() => {
  const selections = configuratorState.value.selections;

  const selectedPlacement = props.decorations.find(
    (d) => d.position.value === selections.selectedPlacementName
  );
  if (!selectedPlacement) {
    return '';
  }

  const selectedTechnique = selectedPlacement.techniques.find(
    (t) => t.systemId === selections.selectedTechnique
  );
  if (!selectedTechnique) {
    return '';
  }

  if (selectedTechnique.variants.length == 1) {
    return selectedTechnique.name;
  }

  const selectedVariant = selectedTechnique.variants.find(
    (v) => v.systemId === selections.selectedTechniqueVariant
  );
  if (!selectedVariant) {
    return '';
  }

  return selectedVariant.name;
});

function displayDesignAddon() {
  return !!designAddon() && designAddon().variants.length > 0;
}

function displayGreetingAddon() {
  return !!greetingAddon() && greetingAddon().variants.length > 0;
}

function displayBoxAddon() {
  return !!boxAddon() && boxAddon().variants.length > 0;
}

const designAddon = () => {
  return props.addons.filter((addon) => addon.id === 'Addon-Design')[0];
};

const greetingAddon = () => {
  return props.addons.filter((addon) => addon.id === 'Addon-Greeting')[0];
};

const boxAddon = () => {
  return props.addons.filter((addon) => addon.id === 'Addon-Box')[0];
};

function resetFields() {
  designAddonComponent.value?.resetAddon();
  greetingAddonComponent.value?.resetAddon();
  boxAddonComponent.value?.resetAddon();
  typeSelectorComponent.value?.resetValues();
}

provide(inputFocusKey, { inputFocus, updateFocusState });
provide(configuratorStateKey, {
  configuratorState,
  updateSelection,
  updateDecorationSteps,
  updateQuantity,
  removeDecoration,
  updateActivePriceBrackets,
  removeUpload,
  updateCustomPrice
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.modal)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(websiteText)('configurator__edit_product', {
            product: __props.baseProductName
          }).value), 1),
          _createElementVNode("button", {
            disabled: !productsEdited.value,
            class: "product-config__modal-save primary-button",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (editProductsInCartGroup()))
          }, [
            _createTextVNode(_toDisplayString(_unref(websiteText)('giftcard__save_changes').value) + " ", 1),
            _createVNode(ConfiguratorChevron, {
              open: true,
              class: "product-config__modal-save-chevron"
            })
          ], 8, _hoisted_4)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_5, [
      _createElementVNode("div", _hoisted_6, [
        (!props.isGiftCard)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["product-config__modal-description", 
            descriptionReadMore.value ||
            !descriptionReadMoreEnabled.value ||
            'product-config__modal-description-short'
          ])
            }, _toDisplayString(description.value), 3))
          : _createCommentVNode("", true),
        (!props.isGiftCard && descriptionReadMoreEnabled.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 1,
              class: "product-config__modal-description-read-more-button",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (toggleReadMoreDescription()))
            }, _toDisplayString(descriptionReadMore.value
              ? _unref(websiteText)('global__read_less').value
              : _unref(websiteText)('global__read_more').value), 1))
          : _createCommentVNode("", true),
        (__props.isGiftCard)
          ? (_openBlock(), _createBlock(GiftcardTypeSelector, {
              key: 2,
              isGiftCard: __props.isGiftCard,
              variants: __props.variants,
              giftCardFreightPrice: props.giftCardFreightPrice,
              current: props.selectedVariant,
              ref_key: "typeSelectorComponent",
              ref: typeSelectorComponent
            }, null, 8, ["isGiftCard", "variants", "giftCardFreightPrice", "current"]))
          : _createCommentVNode("", true),
        _createVNode(ConfiguratorQuantitySelector, {
          variants: getVariantsToShow(),
          supportedVariants: configuratorState.value.supportedVariants,
          selectedDecorationName: selectedDecorationName.value,
          "has-decoration": hasDecoration.value,
          isGiftCard: __props.isGiftCard
        }, null, 8, ["variants", "supportedVariants", "selectedDecorationName", "has-decoration", "isGiftCard"]),
        (!__props.isGiftCard)
          ? (_openBlock(), _createBlock(ConfiguratorPriceBrackets, {
              key: 3,
              product: props.selectedVariant,
              "has-decoration": decorationsExist.value
            }, null, 8, ["product", "has-decoration"]))
          : _createCommentVNode("", true),
        (decorationsExist.value)
          ? (_openBlock(), _createBlock(ConfiguratorProductDecorator, {
              key: 4,
              "out-of-stock": outOfPrintStock.value,
              variants: __props.variants,
              decorations: __props.decorations,
              onStarted: begunDecoration,
              onTechniqueVariantSelected: techniqueVariantSelected
            }, null, 8, ["out-of-stock", "variants", "decorations"]))
          : _createCommentVNode("", true),
        (isClientRender && __props.isGiftCard && displayDesignAddon())
          ? (_openBlock(), _createBlock(GiftcardDesignAddon, {
              key: 5,
              current: props.selectedVariant,
              ref_key: "designAddonComponent",
              ref: designAddonComponent,
              addon: designAddon(),
              onAddAddonSelection: addAddonSelection,
              onRemoveAddonSelection: removeAddonSelection,
              onNavigateToLogin: navigateToLogin
            }, null, 8, ["current", "addon"]))
          : _createCommentVNode("", true),
        (isClientRender && __props.isGiftCard && displayGreetingAddon())
          ? (_openBlock(), _createBlock(GiftcardGreetingAddon, {
              key: 6,
              ref_key: "greetingAddonComponent",
              ref: greetingAddonComponent,
              addon: greetingAddon(),
              onAddAddonSelection: addAddonSelection,
              onRemoveAddonSelection: removeAddonSelection
            }, null, 8, ["addon"]))
          : _createCommentVNode("", true),
        (isClientRender && __props.isGiftCard && displayBoxAddon())
          ? (_openBlock(), _createBlock(GiftcardBoxAddon, {
              key: 7,
              addon: boxAddon(),
              ref_key: "boxAddonComponent",
              ref: boxAddonComponent,
              onAddAddonSelection: addAddonSelection,
              onRemoveAddonSelection: removeAddonSelection
            }, null, 8, ["addon"]))
          : _createCommentVNode("", true),
        (isClientRender && __props.isGiftCard)
          ? (_openBlock(), _createBlock(GiftcardCountrySelector, {
              key: 8,
              variants: __props.variants,
              onChangeRecipientCountry: changeRecipientCountry
            }, null, 8, ["variants"]))
          : _createCommentVNode("", true),
        (isClientRender && __props.isGiftCard)
          ? (_openBlock(), _createBlock(GiftcardDeliverytypeSelector, {
              key: 9,
              variants: __props.variants,
              onChangeGiftcardType: changeGiftcardType
            }, null, 8, ["variants"]))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createVNode(ConfiguratorSummary, {
          "can-add-to-cart": canAddToCart.value,
          "has-decoration": hasDecoration.value,
          "has-addons": hasAddons.value,
          variants: getVariantsToShow(),
          decorations: __props.decorations,
          isGiftCard: __props.isGiftCard,
          giftCardFreightPrice: props.giftCardFreightPrice
        }, null, 8, ["can-add-to-cart", "has-decoration", "has-addons", "variants", "decorations", "isGiftCard", "giftCardFreightPrice"]),
        (!addonMinimumQuantityFullfilled.value)
          ? (_openBlock(), _createBlock(ErrorMessage, {
              key: 0,
              message: _unref(websiteText)('giftcard__addon_summary_quantity_error').value
            }, null, 8, ["message"]))
          : _createCommentVNode("", true),
        (!variantMaximumQuantityFullfilled.value)
          ? (_openBlock(), _createBlock(ErrorMessage, {
              key: 1,
              message: 
            _unref(websiteText)('giftcard__addon_summary_too_many_error', {
              amount: __props.selectedVariant.fields.MaxGiftCardAmount
            }).value
          
            }, null, 8, ["message"]))
          : _createCommentVNode("", true),
        (!__props.modal)
          ? (_openBlock(), _createElementBlock("button", {
              key: 2,
              class: "product-config__add-to-cart primary-button",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (checkDecorationProgressAndAddToCart())),
              disabled: !canAddToCart.value || !orderRequirementsFullfilled.value
            }, _toDisplayString(_unref(websiteText)('global__add_to_cart').value), 9, _hoisted_8))
          : _createCommentVNode("", true),
        (__props.modal)
          ? (_openBlock(), _createElementBlock("button", {
              key: 3,
              disabled: !productsEdited.value,
              class: "product-config__modal-save-checkout primary-button",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (editProductsInCartGroup()))
            }, [
              _createTextVNode(_toDisplayString(_unref(websiteText)('giftcard__save_changes').value) + " ", 1),
              _createVNode(ConfiguratorChevron, {
                open: true,
                class: "product-config__modal-save-checkout-chevron"
              })
            ], 8, _hoisted_9))
          : _createCommentVNode("", true),
        _createVNode(ProductInformation, { selectedVariant: __props.selectedVariant }, null, 8, ["selectedVariant"])
      ])
    ]),
    (unfinishedDecorationModalOpen.value)
      ? (_openBlock(), _createBlock(ModalContainer, {
          key: 1,
          "show-exit-button": true,
          position: "center",
          onCloseModal: closeConfirmationModal
        }, {
          default: _withCtx(() => [
            _createElementVNode("div", _hoisted_10, [
              _createElementVNode("p", _hoisted_11, _toDisplayString(_unref(websiteText)('decoration__unfinished_title').value), 1),
              _createElementVNode("div", _hoisted_12, [
                _createElementVNode("button", {
                  class: "unfinished-decoration__modal-cancel secondary-button",
                  onClick: cancelConfirmation
                }, _toDisplayString(_unref(websiteText)('decoration__unfinished_cancel').value), 1),
                _createElementVNode("button", {
                  class: "unfinished-decoration__modal-accept primary-button",
                  onClick: closeConfirmationModal
                }, _toDisplayString(_unref(websiteText)('decoration__unfinished_accept').value), 1)
              ])
            ])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})